/**
 * SignalsTable - Trading Signals Display
 *
 * Simple text-based table showing trading signals, confidence scores,
 * reasons, and warnings from QuantX strategy analysis.
 */

import React, { memo } from 'react';
import { Box, Typography, Chip, LinearProgress } from '@mui/material';

/**
 * Table row component - Institutional Grade Styling
 */
const TableRow = ({ label, value, valueColor = '#fff' }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 12px',
            borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.25)' }
        }}
    >
        <Typography sx={{ fontSize: '13px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 500 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: valueColor, fontWeight: 700, fontFamily: 'monospace' }}>
            {value}
        </Typography>
    </Box>
);

/**
 * Section header - Institutional Grade Styling
 */
const SectionHeader = ({ title, badge = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: 'rgba(220, 38, 38, 0.12)',
            borderBottom: '1px solid rgba(220, 38, 38, 0.4)'
        }}
    >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', letterSpacing: '0.8px' }}>
            {title}
        </Typography>
        {badge}
    </Box>
);

/**
 * Get signal color
 */
const getSignalColor = (signal) => {
    if (!signal) return '#6b7280';
    const s = signal.toUpperCase();
    if (s === 'BUY' || s === 'STRONG_BUY') return '#22c55e';
    if (s === 'SELL' || s === 'STRONG_SELL') return '#ef4444';
    if (s === 'HOLD' || s === 'NEUTRAL') return '#fbbf24';
    return '#6b7280';
};

/**
 * Get confidence color
 */
const getConfidenceColor = (confidence) => {
    if (!confidence && confidence !== 0) return '#6b7280';
    if (confidence >= 0.8) return '#22c55e';
    if (confidence >= 0.6) return '#4ade80';
    if (confidence >= 0.4) return '#fbbf24';
    return '#ef4444';
};

/**
 * Signal badge component - Institutional Grade Styling
 */
const SignalBadge = ({ signal }) => (
    <Chip
        label={signal || 'N/A'}
        size="small"
        sx={{
            backgroundColor: getSignalColor(signal),
            color: '#fff',
            fontWeight: 700,
            fontSize: '12px',
            height: '24px',
            minWidth: '70px'
        }}
    />
);

/**
 * Confidence progress bar - Institutional Grade Styling
 */
const ConfidenceBar = ({ confidence }) => {
    const percentage = (confidence || 0) * 100;
    const color = getConfidenceColor(confidence);

    return (
        <Box sx={{ width: '100%', padding: '10px 12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '12px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 500 }}>
                    Confidence
                </Typography>
                <Typography sx={{ fontSize: '13px', color, fontWeight: 700, fontFamily: 'monospace' }}>
                    {percentage.toFixed(0)}%
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: 'rgba(55, 65, 81, 0.5)',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: color,
                        borderRadius: 2
                    }
                }}
            />
        </Box>
    );
};

/**
 * List item component for reasons/warnings - Institutional Grade Styling
 */
const ListItem = ({ text, type = 'reason' }) => {
    const isWarning = type === 'warning';
    const icon = isWarning ? '⚠️' : '✓';
    const color = isWarning ? '#fbbf24' : '#22c55e';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                padding: '6px 12px',
                borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                '&:last-child': { borderBottom: 'none' }
            }}
        >
            <Typography sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                {icon}
            </Typography>
            <Typography sx={{ fontSize: '12px', color, lineHeight: 1.6, fontFamily: 'monospace' }}>
                {text}
            </Typography>
        </Box>
    );
};

/**
 * SignalsTable Component
 */
const SignalsTable = memo(({ data, currentState }) => {
    // Extract data with fallbacks
    const signal = data?.signal || 'NEUTRAL';
    const confidence = data?.confidence;
    const reasons = data?.reasons || [];
    const warnings = data?.warnings || [];
    const entryZone = data?.entryZone || {};
    const timestamp = data?.timestamp;

    const formatPrice = (value) => {
        if (!value && value !== 0) return '--';
        return typeof value === 'number' ? value.toFixed(2) : value;
    };

    const formatTime = (ts) => {
        if (!ts) return '--';
        try {
            return new Date(ts).toLocaleTimeString();
        } catch {
            return '--';
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Signal Summary */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader
                    title="TRADING SIGNAL"
                    badge={<SignalBadge signal={signal} />}
                />
                <TableRow
                    label="Signal"
                    value={signal}
                    valueColor={getSignalColor(signal)}
                />
                <TableRow
                    label="Generated At"
                    value={formatTime(timestamp)}
                    valueColor="#9ca3af"
                />
                <TableRow
                    label="Session"
                    value={currentState?.session || '--'}
                    valueColor="#fbbf24"
                />

                {/* Confidence Bar */}
                <ConfidenceBar confidence={confidence} />
            </Box>

            {/* Entry Zone */}
            {(entryZone.idealEntry || entryZone.fibLevel) && (
                <Box
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #374151',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}
                >
                    <SectionHeader title="ENTRY ZONE" />
                    <TableRow
                        label="Ideal Entry"
                        value={formatPrice(entryZone.idealEntry)}
                        valueColor={signal === 'BUY' ? '#22c55e' : signal === 'SELL' ? '#ef4444' : '#fff'}
                    />
                    <TableRow
                        label="Fib Level"
                        value={entryZone.fibLevel || '--'}
                        valueColor="#fbbf24"
                    />
                    <TableRow
                        label="Fib Price"
                        value={formatPrice(entryZone.fibPrice)}
                        valueColor="#06b6d4"
                    />
                </Box>
            )}

            {/* Reasons */}
            {reasons.length > 0 && (
                <Box
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #374151',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}
                >
                    <SectionHeader title="REASONS" badge={
                        <Chip
                            label={reasons.length}
                            size="small"
                            sx={{
                                backgroundColor: '#22c55e',
                                color: '#fff',
                                fontSize: '9px',
                                height: '16px',
                                minWidth: '20px'
                            }}
                        />
                    } />
                    {reasons.map((reason, index) => (
                        <ListItem key={index} text={reason} type="reason" />
                    ))}
                </Box>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
                <Box
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(251, 191, 36, 0.3)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}
                >
                    <SectionHeader title="WARNINGS" badge={
                        <Chip
                            label={warnings.length}
                            size="small"
                            sx={{
                                backgroundColor: '#fbbf24',
                                color: '#000',
                                fontSize: '9px',
                                height: '16px',
                                minWidth: '20px'
                            }}
                        />
                    } />
                    {warnings.map((warning, index) => (
                        <ListItem key={index} text={warning} type="warning" />
                    ))}
                </Box>
            )}

            {/* No Reasons/Warnings */}
            {reasons.length === 0 && warnings.length === 0 && (
                <Box
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #374151',
                        borderRadius: '4px',
                        padding: '12px',
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                        No additional analysis available
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

SignalsTable.displayName = 'SignalsTable';

export default SignalsTable;
